import { btn, h, icon, type IconName } from './icons';
import { words } from './words';

export type PanelId = 'size' | 'colours' | 'frames' | 'order' | 'engrave';
export interface FramesViewHandle { root: HTMLElement; reveal?: () => void; setKept?: (text: string) => void }
/** A110V « Je veux cette paire »: told when it becomes visible (takes the photo) and hidden. */
export interface OrderViewHandle { root: HTMLElement; onShow?: () => void; onHide?: () => void }
/** A110Z « Gravure » (desktop only: column tab + the temple band under the face; phones use the full-screen view). */
export interface EngraveViewHandle { root: HTMLElement; band: HTMLElement; onShow?: () => void; onHide?: () => void }
const PANEL_ORDER: readonly PanelId[] = ['size', 'colours', 'frames', 'order'];

/** Desktop layout (rail + column) from this size; below it, the mobile pill + panel. */
const DESKTOP_QUERY = '(min-width: 1024px) and (min-height: 600px)';
const CLOSE_MS = 140;
/**
 * A42F readable glass: mean camera luma (0–255) behind the controls under which
 * the glass turns more opaque, and above which it returns to the design value.
 * At .36 white the ink text drops under 4.5:1 below a luma of about 110.
 */
const DIM_ON_LUMA = 125;
const DIM_OFF_LUMA = 145;

/** Height of Shopify's theme-preview bar (unpublished themes only) when docked over the bottom of the viewport. */
function previewBarInset(): number {
  const bar = document.getElementById('PBarNextFrameWrapper') ?? document.getElementById('preview-bar-iframe') ?? document.querySelector<HTMLElement>('#PreviewBar, .shopify-preview-bar');
  if (!bar) return 0;
  const r = bar.getBoundingClientRect();
  if (!r.height || r.top >= innerHeight || r.bottom < innerHeight - 2) return 0;
  return Math.min(120, Math.max(0, Math.round(innerHeight - r.top)));
}

/**
 * A42E « Essayage B2 » controls: a floating frosted-glass pill (Taille ·
 * Couleurs · Modèles). Mobile: one compact panel opens above the pill.
 * Desktop: models in a left rail, settings in a right column. The camera stays
 * the hero: nothing covers the central part of the screen.
 */
export class ControlShell {
  readonly root = h('div', 'mt-controls');
  private readonly pill = h('div', 'mt-pill');
  private readonly pillItems = new Map<PanelId, HTMLButtonElement>();
  private readonly sheet = h('div', 'mt-sheet');
  private readonly rail = h('aside', 'mt-rail');
  private readonly column = h('aside', 'mt-column');
  private readonly columnBody = h('div', 'mt-column-body');
  private readonly columnTabs = new Map<PanelId, HTMLButtonElement>();
  private readonly help = h('div', 'mt-help');
  private readonly helpRear = h('p', 'mt-help-note');
  private readonly media = window.matchMedia(DESKTOP_QUERY);
  private views: { size: HTMLElement | null; colours: HTMLElement | null; frames: FramesViewHandle | null; order: OrderViewHandle | null; engrave: EngraveViewHandle | null } = { size: null, colours: null, frames: null, order: null, engrave: null };
  private panel: PanelId | null = null;
  private orderHref: (() => string | null) | null = null;
  private railOpen = true;
  private desktop = false;
  private helpOpen = false;
  private closeTimer = 0;
  private readonly timer: number;
  private readonly resize = new ResizeObserver(() => this.layout());
  private probe: CanvasRenderingContext2D | null = null;
  private dim = false;

  constructor(private readonly overlay: HTMLElement, private readonly stage: HTMLElement, private readonly helpButton: HTMLButtonElement) {
    const w = words();
    this.root.hidden = true;

    // Pill toolbar
    this.pill.setAttribute('role', 'toolbar');
    this.pill.setAttribute('aria-label', w.toolbar);
    const items: Array<[PanelId, IconName, string]> = [['size', 'size', w.pill[0]], ['colours', 'colours', w.pill[1]], ['frames', 'frames', w.pill[2]], ['order', 'heart', w.pill[3]]];
    for (const [id, iconName, label] of items) {
      const b = btn('mt-pill-item');
      b.dataset.panel = id;
      b.setAttribute('aria-pressed', 'false');
      b.append(icon(iconName), h('span', 'mt-pill-label', label));
      b.onclick = () => this.tapPill(id);
      this.pillItems.set(id, b);
      this.pill.append(b);
    }

    // Mobile panel
    this.sheet.hidden = true;
    this.sheet.setAttribute('role', 'region');
    this.installSwipeToClose();

    // Desktop column: Size | Colours | Gravure (A110Z) | « Je la veux » tabs + close
    const head = h('div', 'mt-column-head');
    const tabs = h('div', 'mt-seg mt-dtabs'); tabs.setAttribute('role', 'tablist');
    for (const [id, label] of [['size', w.pill[0]], ['colours', w.pill[1]], ['engrave', w.engrave.tab], ['order', w.pill[3]]] as Array<[PanelId, string]>) {
      const t = btn('mt-seg-item mt-dtab', label);
      t.setAttribute('role', 'tab'); t.dataset.panel = id;
      t.onclick = () => this.open(id);
      this.columnTabs.set(id, t); tabs.append(t);
    }
    const closeColumn = btn('mt-icon-btn mt-column-close');
    closeColumn.setAttribute('aria-label', w.closePanel);
    closeColumn.append(icon('close', 16));
    closeColumn.onclick = () => this.open(null);
    head.append(tabs, closeColumn);
    this.column.append(head, this.columnBody);
    this.column.hidden = true;
    this.rail.hidden = true;

    // Help popover
    this.help.id = `mt-help-${Math.random().toString(36).slice(2, 8)}`;
    this.help.hidden = true;
    this.help.append(h('p', 'mt-help-title', w.help), h('p', 'mt-help-body', w.helpBody));
    const note = h('p', 'mt-help-note', w.helpNote);
    this.helpRear.textContent = w.helpRear; this.helpRear.hidden = true;
    this.help.append(note, this.helpRear);
    helpButton.setAttribute('aria-expanded', 'false');
    helpButton.setAttribute('aria-controls', this.help.id);
    helpButton.addEventListener('click', () => this.setHelp(!this.helpOpen));

    this.root.append(this.rail, this.column, this.sheet, this.pill);
    stage.append(this.root, this.help);

    // Tapping the camera closes the panel and the help; Esc too (desktop).
    stage.addEventListener('click', this.onStageClick);
    document.addEventListener('pointerdown', this.onOutsidePointer, true);
    window.addEventListener('keydown', this.onKey);
    this.media.addEventListener('change', this.onMedia);
    this.resize.observe(this.pill); this.resize.observe(this.sheet);
    this.desktop = this.media.matches;
    this.placeViews();
    this.sync();
    // Shopify's preview bar appears a few seconds after page load and can be folded away.
    this.timer = window.setInterval(() => { if (!this.overlay.isConnected) this.destroy(); else { this.layout(); this.sampleBackdrop(); } }, 1000);
  }

  /** Controls appear once the camera runs. */
  setReady(ready: boolean): void { this.root.hidden = !ready; this.layout(); if (ready) this.sampleBackdrop(); }

  setSizeView(el: HTMLElement | null): void { this.replace('size', el); }
  setColoursView(el: HTMLElement | null): void { this.replace('colours', el); }
  setFramesView(view: FramesViewHandle | null): void {
    this.views.frames?.root.remove();
    this.views.frames = view;
    this.placeViews(); this.sync();
  }
  /** A110Z: the « Gravure » view — desktop column tab and the temple band under the face. */
  setEngraveView(view: EngraveViewHandle | null): void {
    if (this.views.engrave && this.panel === 'engrave') this.views.engrave.onHide?.();
    this.views.engrave?.root.remove();
    this.views.engrave?.band.remove();
    this.views.engrave = view;
    if (view) { view.band.hidden = true; this.rail.after(view.band); }
    if (this.panel === 'engrave' && !view) this.panel = null;
    this.placeViews(); this.sync();
  }
  /** Desktop layout (rail + column) rather than the phone pill + panel. */
  get isDesktop(): boolean { return this.desktop; }
  /** Current panel (null when closed). */
  get current(): PanelId | null { return this.panel; }

  /** Dismiss the foremost local panel before closing the whole try-on. */
  dismiss(): boolean {
    if (this.helpOpen) { this.setHelp(false); return true; }
    if (this.panel) { this.open(null); return true; }
    return false;
  }
  /** A110V: the « Je veux cette paire » view (4th pill item, 3rd desktop tab). */
  setOrderView(view: OrderViewHandle | null): void {
    if (this.views.order && this.panel === 'order') this.views.order.onHide?.();
    this.views.order?.root.remove();
    this.views.order = view;
    if (this.panel === 'order' && !view) this.panel = null;
    this.placeViews(); this.sync();
  }
  setLaminated(laminated: boolean): void { this.helpRear.hidden = !laminated; }
  /** Evaluated on every click so a model or colour change uses its current product. */
  setOrderHref(resolve: () => string | null): void { this.orderHref = resolve; }
  setKept(text: string): void { this.views.frames?.setKept?.(text); }

  /** Opens a panel (mobile) or the column on that tab (desktop); null closes. */
  open(panel: PanelId | null): void {
    if (panel === 'order') {
      const href = this.orderHref?.();
      if (href) {
        // The catalogue may be embedded in the Shopify page: leave the iframe too.
        const link = document.createElement('a');
        link.href = href; link.target = '_top';
        link.click();
        return;
      }
    }
    if (panel && !this.available(panel)) panel = null;
    if (this.desktop && panel === 'frames') { this.railOpen = true; this.sync(); return; }
    const previous = this.panel;
    this.panel = panel;
    this.sync(previous);
  }

  destroy(): void {
    window.clearInterval(this.timer);
    this.resize.disconnect();
    this.stage.removeEventListener('click', this.onStageClick);
    document.removeEventListener('pointerdown', this.onOutsidePointer, true);
    window.removeEventListener('keydown', this.onKey);
    this.media.removeEventListener('change', this.onMedia);
  }

  private available(panel: PanelId): boolean {
    // Gravure lives in the desktop column only; on phones it is a full-screen view outside the shell.
    if (panel === 'engrave') return this.desktop && Boolean(this.views.engrave);
    return panel === 'size' ? Boolean(this.views.size) : panel === 'colours' ? Boolean(this.views.colours) : panel === 'order' ? Boolean(this.views.order) : Boolean(this.views.frames);
  }

  private replace(kind: 'size' | 'colours', el: HTMLElement | null): void {
    this.views[kind]?.remove();
    this.views[kind] = el;
    if (this.panel === kind && !el) this.panel = null;
    this.placeViews(); this.sync();
  }

  private tapPill(id: PanelId): void {
    if (this.desktop && id === 'frames') { this.railOpen = !this.railOpen; this.sync(); return; }
    this.open(this.panel === id ? null : id);
  }

  /** Mobile: every view lives in the panel. Desktop: models in the rail, settings in the column. */
  private placeViews(): void {
    const { size, colours, frames, order, engrave } = this.views;
    if (this.desktop) {
      if (frames && frames.root.parentElement !== this.rail) this.rail.append(frames.root);
      for (const v of [size, colours, engrave?.root, order?.root]) if (v && v.parentElement !== this.columnBody) this.columnBody.append(v);
    } else {
      for (const v of [size, colours, frames?.root, order?.root]) if (v && v.parentElement !== this.sheet) this.sheet.append(v);
      // The desktop-only view never shows in the phone panel.
      if (engrave) { engrave.root.remove(); engrave.root.hidden = true; }
    }
  }

  private sync(previous: PanelId | null = this.panel): void {
    const { size, colours, frames, order, engrave } = this.views;
    this.overlay.classList.toggle('mt-root--desktop', this.desktop);
    this.pillItems.get('colours')!.hidden = !colours;
    this.pillItems.get('frames')!.hidden = !frames;
    this.pillItems.get('size')!.hidden = !size;
    this.pillItems.get('order')!.hidden = !order;
    this.pill.dataset.items = String(Array.from(this.pillItems.values()).filter(b => !b.hidden).length);
    for (const [id, b] of this.pillItems) {
      const on = this.desktop && id === 'frames' ? this.railOpen && Boolean(frames) : this.panel === id;
      b.setAttribute('aria-pressed', String(on));
    }
    // Which view is visible
    if (size) size.hidden = this.panel !== 'size';
    if (colours) colours.hidden = this.panel !== 'colours';
    if (frames) frames.root.hidden = this.desktop ? false : this.panel !== 'frames';
    if (order) order.root.hidden = this.panel !== 'order';
    if (engrave) { engrave.root.hidden = this.panel !== 'engrave'; engrave.band.hidden = !(this.desktop && this.panel === 'engrave'); }
    const shown = this.panel === 'size' ? size : this.panel === 'colours' ? colours : this.panel === 'order' ? order?.root : this.panel === 'engrave' ? engrave?.root : this.panel === 'frames' && !this.desktop ? frames?.root : null;
    if (shown && previous !== this.panel && previous !== null) {
      shown.classList.remove('mt-view--enter'); void shown.offsetWidth; shown.classList.add('mt-view--enter');
    }
    if (this.panel === 'frames' || (this.desktop && frames && this.railOpen)) requestAnimationFrame(() => frames?.reveal?.());

    if (this.desktop) {
      this.sheet.hidden = true; this.sheet.classList.remove('mt-sheet--open');
      this.rail.hidden = !(frames && this.railOpen);
      this.column.hidden = !(this.panel === 'size' || this.panel === 'colours' || this.panel === 'order' || this.panel === 'engrave');
      this.columnTabs.get('colours')!.hidden = !colours;
      this.columnTabs.get('engrave')!.hidden = !engrave;
      this.columnTabs.get('order')!.hidden = !order;
      // A110Z: four tabs must share the head with the close button (compact spacing).
      const tabs = this.columnTabs.get('size')!.parentElement;
      if (tabs) tabs.dataset.count = String(Array.from(this.columnTabs.values()).filter(t => !t.hidden).length);
      for (const [id, t] of this.columnTabs) { const on = this.panel === id; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; }
    } else {
      this.rail.hidden = true; this.column.hidden = true;
      this.setSheet(this.panel !== null);
    }
    this.sheet.setAttribute('aria-label', this.panel === 'order' ? words().order.title : this.panel === 'engrave' ? words().engrave.title : this.panel ? words().pill[PANEL_ORDER.indexOf(this.panel)]! : '');
    this.layout();
    // A110V: the order view takes its photo when it appears, and stops its countdown when it goes.
    if (previous !== this.panel) {
      if (previous === 'order') order?.onHide?.();
      if (this.panel === 'order') order?.onShow?.();
      if (previous === 'engrave') engrave?.onHide?.();
      if (this.panel === 'engrave') engrave?.onShow?.();
    }
  }

  private setSheet(open: boolean): void {
    window.clearTimeout(this.closeTimer);
    if (open) {
      if (this.sheet.hidden) {
        this.sheet.hidden = false;
        void this.sheet.offsetWidth; // start the open transition from its closed state
      }
      this.sheet.classList.add('mt-sheet--open');
    } else if (!this.sheet.hidden) {
      this.sheet.classList.remove('mt-sheet--open');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.closeTimer = window.setTimeout(() => { if (!this.panel) { this.sheet.hidden = true; this.layout(); } }, reduced ? 0 : CLOSE_MS);
    }
  }

  private setHelp(open: boolean): void {
    this.helpOpen = open;
    this.help.hidden = !open;
    this.helpButton.setAttribute('aria-expanded', String(open));
  }

  /** Bottom offsets: Shopify preview bar, then the guidance stays above the pill and the open panel. */
  private layout(): void {
    const inset = previewBarInset();
    this.overlay.style.setProperty('--mt-preview-inset', `${inset}px`);
    if (this.root.hidden) { this.overlay.style.setProperty('--mt-bottom-offset', `${inset}px`); return; }
    const stageRect = this.stage.getBoundingClientRect();
    let top = this.pill.getBoundingClientRect().top;
    if (!this.desktop && !this.sheet.hidden) top = Math.min(top, this.sheet.getBoundingClientRect().top);
    const offset = Math.max(0, Math.round(stageRect.bottom - top));
    if (offset) this.overlay.style.setProperty('--mt-bottom-offset', `${offset}px`);
  }

  /**
   * A42F readable glass. Once a second, the camera image is reduced to 32 × 32
   * pixels and its mean luma is read where the controls sit (mobile: title band
   * and the bottom panel; desktop: rail, column and pill). Dark there → the glass
   * gets more opaque (`mt-ui--dim`). Read-only: the camera, tracking and 3D are untouched.
   */
  private sampleBackdrop(): void {
    if (this.root.hidden || document.hidden) return;
    const video = this.stage.querySelector<HTMLVideoElement>('video.mt-video');
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
    const box = this.stage.getBoundingClientRect();
    if (!box.width || !box.height) return;
    try {
      if (!this.probe) {
        const canvas = document.createElement('canvas');
        canvas.width = 32; canvas.height = 32;
        this.probe = canvas.getContext('2d', { willReadFrequently: true });
        if (!this.probe) return;
      }
      // Visible part of the video (object-fit: cover; anchored to the bottom on mobile).
      const vw = video.videoWidth, vh = video.videoHeight;
      const scale = Math.max(box.width / vw, box.height / vh);
      const sw = box.width / scale, sh = box.height / scale;
      const sx = (vw - sw) / 2, sy = (vh - sh) * (this.desktop ? 0.5 : 1);
      this.probe.drawImage(video, sx, sy, sw, sh, 0, 0, 32, 32);
      const data = this.probe.getImageData(0, 0, 32, 32).data;
      const mean = (x0: number, x1: number, y0: number, y1: number) => {
        let sum = 0, n = 0;
        for (let y = Math.floor(y0 * 32); y < Math.ceil(y1 * 32); y++) for (let x = Math.floor(x0 * 32); x < Math.ceil(x1 * 32); x++) {
          const i = (y * 32 + x) * 4;
          sum += 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!; n++;
        }
        return n ? sum / n : 255;
      };
      // Mirrored or not, the bands are symmetric enough: rail and column are both tested.
      const bands = this.desktop
        ? [mean(0, 0.17, 0.08, 0.98), mean(0.74, 1, 0.08, 0.98), mean(0.33, 0.67, 0.86, 1)]
        : [mean(0, 1, 0, 0.14), mean(0, 1, 0.56, 1)];
      const luma = Math.min(...bands);
      this.overlay.dataset.backdropLuma = String(Math.round(luma));
      const dim = this.dim ? luma < DIM_OFF_LUMA : luma < DIM_ON_LUMA;
      if (dim !== this.dim) { this.dim = dim; this.overlay.classList.toggle('mt-ui--dim', dim); }
    } catch {
      // A camera frame that cannot be read leaves the glass as it is.
    }
  }

  private readonly onStageClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    if (!target || target.closest('.mt-controls, .mt-help, .mt-topbar, .mt-debug, .mt-phone-measure, .mt-model-switch-status, .mt-eng-screen, .mt-eng-toast')) return;
    if (this.helpOpen) this.setHelp(false);
    if (this.panel) this.open(null);
  };

  private readonly onOutsidePointer = (event: PointerEvent) => {
    if (!this.helpOpen) return;
    const target = event.target as Node | null;
    if (target && (this.help.contains(target) || this.helpButton.contains(target))) return;
    this.setHelp(false);
  };

  private readonly onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (this.helpOpen) { this.setHelp(false); event.stopPropagation(); return; }
    if (this.panel) { this.open(null); event.stopPropagation(); }
  };

  private readonly onMedia = () => {
    this.desktop = this.media.matches;
    if (this.desktop && this.panel === 'frames') { this.panel = null; this.railOpen = true; }
    if (!this.desktop && this.panel === 'engrave') { this.views.engrave?.onHide?.(); this.panel = null; }
    this.placeViews(); this.sync();
  };

  /** Swipe down on the mobile panel closes it (vertical gesture only). */
  private installSwipeToClose(): void {
    // Touch events (not pointer events): a pan that scrolls a list must not cancel the gesture.
    let startX = 0, startY = 0, tracking = false;
    this.sheet.addEventListener('touchstart', e => {
      const touch = e.touches[0];
      // Only a pull down from the top of the panel closes it, never a scroll inside it.
      tracking = e.touches.length === 1 && this.sheet.scrollTop <= 0 && Boolean(touch);
      if (touch) { startX = touch.clientX; startY = touch.clientY; }
    }, { passive: true });
    this.sheet.addEventListener('touchend', e => {
      if (!tracking) return; tracking = false;
      const touch = e.changedTouches[0];
      if (!touch) return;
      const dy = touch.clientY - startY, dx = Math.abs(touch.clientX - startX);
      if (dy > 48 && dx < 40) this.open(null);
    }, { passive: true });
    this.sheet.addEventListener('touchcancel', () => { tracking = false; }, { passive: true });
  }
}
